import { ProductCard } from '@/components/catalogue/products/cards/ProductCard';
import { Button } from '@/components/form/Button';
import { useModal } from '@/components/modal/useModal';
import SiArrowRight from '@/icons/SiArrowRight';
import { Container } from '@/layouts/main/Main';
import { PriceHistoryModal } from '@/pages/product/price/PriceHistoryModal';
import { useContractApproval, useUpdateContractApproval } from '@/services/api/hooks/companies';
import { useCatalogDiff } from '@/services/api/hooks/product';
import { RoleType, useCompanyId, usePartnerId } from '@/services/authentication/hooks/user';
import { CatalogueProvider } from '@/services/catalogue/CatalogueService';
import { FC, PropsWithChildren, useEffect } from 'react';
import { useNavigate } from 'react-router';

const ApprovalContainer: FC<PropsWithChildren> = ({ children }) => {
  return <div className="px-4 sm:px-8 w-full py-8">{children}</div>;
};

export const SelfCompanyContractApprovalDisplay: FC = () => {
  const partnerId = usePartnerId();
  const companyId = useCompanyId();
  if (!partnerId || !companyId) throw new Error('Missing partner or company id');

  const [diff] = useCatalogDiff(companyId, partnerId);
  const priceHistoryModalProps = useModal<{
    productId: string;
  }>();
  const updateApproval = useUpdateContractApproval();
  const [contractApprovalResponse] = useContractApproval(partnerId, companyId);
  const navigate = useNavigate();
  const onApproved = async () => {
    await updateApproval.mutateAsync({
      partnerId: partnerId,
      customerId: companyId,
      approved: true
    });
  };
  const onRejected = async () => {
    await updateApproval.mutateAsync({
      partnerId: partnerId,
      customerId: companyId,
      approved: false
    });
  };

  useEffect(() => {
    if (!contractApprovalResponse.needsApproval) navigate('/');
  }, [contractApprovalResponse]);

  return (
    <>
      <CatalogueProvider
        configuration={{ for: RoleType.Company, as: RoleType.Company, companyId: companyId, partnerId: partnerId }}
      >
        <PriceHistoryModal {...priceHistoryModalProps} />
        <div className="border-secondary-std border-b">
          <Container animate="fade" className="w-full py-3">
            <h2 className="font-bold text-3xl">Catalogue Changes</h2>
            <span>
              There have been recent changes that potentially affect your bundles. Please review the changes and accept
              or reject the updates.
            </span>
          </Container>
        </div>
        {diff.added.length > 0 && (
          <ApprovalContainer>
            <h2 className="font-semibold text-2xl flex-1">New products</h2>
            <span>The following products have recently been added to your catalog</span>
            <div className="grid h-fit grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 overflow-y-auto p-6">
              {diff.added.map(product => (
                <ProductCard
                  key={`diffAdded-${product.id}`}
                  product={product}
                  priceHistoryModalProps={priceHistoryModalProps}
                />
              ))}
            </div>
          </ApprovalContainer>
        )}
        {diff.removed.length > 0 && (
          <ApprovalContainer>
            <h2 className="font-semibold text-2xl flex-1">Removed products</h2>
            <span>The following products have recently been removed from your catalog</span>
            <div className="grid h-fit grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 overflow-y-auto p-6">
              {diff.removed.map(product => (
                <ProductCard
                  key={`diffRemoved-${product.id}`}
                  product={product}
                  priceHistoryModalProps={priceHistoryModalProps}
                />
              ))}
            </div>
          </ApprovalContainer>
        )}
        {diff.changed.length > 0 && (
          <ApprovalContainer>
            <h2 className="font-semibold text-2xl flex-1">Updated products</h2>
            <span>The following products in your bundles have recently been updated.</span>
            <div className="flex flex-col gap-12 overflow-y-auto p-6">
              {diff.changed.map((product, index) => (
                <div className="flex gap-4" key={`updated-product-${index}`}>
                  <ProductCard
                    key={`diffChangedOld-${product.old.id}`}
                    product={product.old}
                    priceHistoryModalProps={priceHistoryModalProps}
                  />
                  <div className="flex items-center justify-center font-semibold flex-1">
                    <SiArrowRight className="text-3xl" />
                  </div>
                  <ProductCard
                    key={`diffChangedNew-${product.new.id}`}
                    product={product.new}
                    priceHistoryModalProps={priceHistoryModalProps}
                  />
                </div>
              ))}
            </div>
          </ApprovalContainer>
        )}
        <div className="flex flex-col items-center justify-center max-w-7xl px-4 sm:px-8 w-full py-8">
          <div className="flex gap-4">
            <Button loading={updateApproval.isLoading} className="h-fit" onClick={onApproved}>
              <span>Accept changes</span>
            </Button>
            <Button loading={updateApproval.isLoading} className="h-fit" onClick={onRejected}>
              <span>Reject changes</span>
            </Button>
          </div>
        </div>
      </CatalogueProvider>
    </>
  );
};
